import {
  DELIVERABLE_PRIORITY_LABELS,
  DELIVERABLE_STATUS_LABELS,
  type DeliverablePriority,
  type DeliverableStatus,
} from "@dream/config/constants";
import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "../lib/cn.ts";
import { priorityColors, statusColors } from "../tokens/tokens.ts";
import { IconClock, IconRepeat, IconSend } from "./icons.tsx";

/**
 * Badges de status e prioridade.
 *
 * As cores vêm de `statusColors` (labels pastel do Ofative). O ponto colorido
 * acompanha o texto: cor sozinha não é informação acessível.
 */

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  children: ReactNode;
  tone?: "neutral" | "brand" | "success" | "warning" | "danger" | "info";
  size?: "sm" | "md";
}

const TONES = {
  neutral:
    "bg-[var(--dream-badge-neutral-bg)] text-[var(--dream-badge-neutral-fg)] border-[var(--dream-badge-neutral-border)]",
  brand: "bg-[var(--dream-info-bg)] text-[var(--dream-info-fg)] border-[var(--dream-info-border)]",
  success:
    "bg-[var(--dream-success-bg)] text-[var(--dream-success-fg)] border-[var(--dream-success-border)]",
  warning:
    "bg-[var(--dream-warning-bg)] text-[var(--dream-warning-fg)] border-[var(--dream-warning-border)]",
  danger:
    "bg-[var(--dream-danger-bg)] text-[var(--dream-danger-fg)] border-[var(--dream-danger-border)]",
  info: "bg-[var(--dream-info-bg)] text-[var(--dream-info-fg)] border-[var(--dream-info-border)]",
} as const;

export function Badge({ children, tone = "neutral", size = "sm", className, ...rest }: BadgeProps) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 border font-semibold whitespace-nowrap tracking-[0]",
        "rounded-[var(--radius-full,9999px)]",
        "transition-[background-color,color,border-color] duration-[var(--dream-duration-fast)] ease-[var(--dream-ease-standard)]",
        size === "sm" ? "h-5 px-2 text-[0.6875rem]" : "h-6 px-2.5 text-xs",
        TONES[tone],
        className,
      )}
      {...rest}
    >
      {children}
    </span>
  );
}

export function StatusBadge({
  status,
  size = "sm",
  className,
}: {
  status: DeliverableStatus;
  size?: "sm" | "md";
  className?: string;
}) {
  const palette = statusColors.values[status];

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 border font-semibold whitespace-nowrap tracking-[0]",
        "rounded-[var(--radius-full,9999px)]",
        size === "sm" ? "h-5 px-2 text-[0.6875rem]" : "h-6 px-2.5 text-xs",
        className,
      )}
      style={{
        backgroundColor: palette.bg,
        color: palette.fg,
        borderColor: palette.border,
      }}
    >
      <span
        aria-hidden="true"
        className="size-1.5 shrink-0 rounded-full"
        style={{ backgroundColor: palette.dot }}
      />
      {DELIVERABLE_STATUS_LABELS[status]}
    </span>
  );
}

export function LateBadge({ className }: { className?: string }) {
  return (
    <Badge tone="danger" className={className}>
      <IconClock className="size-3 shrink-0" />
      Atrasada
    </Badge>
  );
}

export function PublishBadge({ className }: { className?: string }) {
  return (
    <Badge tone="info" className={className}>
      <IconSend className="size-3 shrink-0" />
      Publicação
    </Badge>
  );
}

export function RecurringBadge({ className }: { className?: string }) {
  return (
    <Badge tone="brand" className={className}>
      <IconRepeat className="size-3 shrink-0" />
      Série
    </Badge>
  );
}

export function PriorityBadge({
  priority,
  size = "sm",
  className,
}: {
  priority: DeliverablePriority;
  size?: "sm" | "md";
  className?: string;
}) {
  const palette = priorityColors.values[priority];

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 border font-semibold whitespace-nowrap tracking-[0]",
        "rounded-[var(--radius-full,9999px)]",
        size === "sm" ? "h-5 px-2 text-[0.6875rem]" : "h-6 px-2.5 text-xs",
        className,
      )}
      style={{
        backgroundColor: palette.bg,
        color: palette.fg,
        borderColor: palette.border,
      }}
    >
      {DELIVERABLE_PRIORITY_LABELS[priority]}
    </span>
  );
}
